import StackTrace from "stacktrace-js";

const priorErrors = new Map<string, number>();
let priorIrrelevantErrors = 0;

function IsIrrelevantError(event: ErrorEvent, frames: StackTrace.StackFrame[] | undefined): boolean {
    if (!event.filename && event.lineno === 0 && event.colno === 0 && event.message === 'Script error.') {
        // cross-domain JS error; likely browser extensions since we don't allow cross domain scripts
        //https://html.spec.whatwg.org/multipage/webappapis.html#runtime-script-errors (muted errors; point 4)
        return true;
    } else if (event.lineno === 0 && event.colno === 0 && event.message === 'ResizeObserver loop completed with undelivered notifications.') {
        // something, possibly react related and possibly browser extension related, that triggers resizes in a resize event.
        // discussion: https://sentry.io/answers/react-resizeobserver-loop-completed-with-undelivered-notifications/
        return true;
    } else if (event.message === 'TypeError: undefined is not an object (evaluating \'a.L\')') {
        //iOS google chrome translate error https://sentry.io/changelog/browser-extension-filters-new-additions/
        return true;
    } else if (event.message.startsWith("Uncaught EvalError: Refused to evaluate a string as JavaScript because 'unsafe-eval'")) {
        //Likely addon or odd browser trying to inject scripts e.g. https://github.com/jgraph/drawio-desktop/issues/1875
        return true;
    } else if (!event.filename || !event.filename.includes("/dist/app.js?v=")) {
        // we only care about errors that originate from our own app bundle, not those of extensions or scripts injected by a user
        return true;
    } else if (event.filename && event.filename.startsWith('chrome-extension://')) {
        //don't care about chrome extensions
        return true;
    } else if (frames && frames[0]) {
        if (event.message === 'NS_ERROR_UNEXPECTED: '
            && frames[0].fileName && frames[0].fileName.includes('/tinymce/')
            && navigator.userAgent.includes('Firefox/')) {
            // 09-12-2024: waarschijnlijk irrelevant want dit is sinds oktober 2024 niet meer voorgekomen
            //weird bug in TinyMCE: https://github.com/tinymce/tinymce/issues/2671
            //weird bug in TinyMCE: https://github.com/tinymce/tinymce/issues/3113
            return true;
        }

        for (const frame of frames) {
            if (frame.fileName?.startsWith("chrome-extension://")) {
                //don't care about chrome extensions
                return true;
            } else if (frame.source?.startsWith("    at <anonymous>:")) {
                // likely an extension or a custom user script (like Tampermonkey)
                return true;
            }
        }

        if (frames[0].source?.startsWith('checkDCE/')
            && event.error?.message?.startsWith('React is running in production mode, but dead code elimination has not been applied.')) {
            //ongelukkige interactie tussen esbuild en react: https://github.com/evanw/esbuild/issues/2353
            return true;
        }
    }
    //take a look at https://github.com/getsentry/relay/blob/master/relay-filter/src/browser_extensions.rs#L9-L76
    //for more ideas on what to block.

    return false;
}

function ErrorKindCount(event: ErrorEvent, isIrrelevant: boolean): number {
    if (isIrrelevant) {
        return ++priorIrrelevantErrors;
    }
    const key = `${event.message}\t${event.filename}\t${event.lineno}\t${event.colno}\t${event?.error?.stack}`;
    const errorCount = (priorErrors.get(key) ?? 0) + 1;
    priorErrors.set(key, errorCount);
    return errorCount;
}

export function addErrorHandler() {
    addEventListener("error", event => {

        const errorBasics = {
            url: location.href,
            userAgent: navigator.userAgent,
            message: event.message,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            rawStack: event.error?.stack,
            currentDocument: document.documentElement.outerHTML.substring(0, 100_000),
        };

        (window as any).lastJsCrashForSelenium = errorBasics;

        StackTrace
            .fromError(event.error)
            .then(frames => ({ frames }))
            .catch(() => ({ frames: undefined, unusualError: JSON.stringify(event.error) }))
            .then(errorExtraInfo => {

                const isIrrelevant = IsIrrelevantError(event, errorExtraInfo.frames);
                const errorCount = ErrorKindCount(event, isIrrelevant);
                const isPowerOfTwo = !(errorCount & (errorCount - 1));

                if (!isPowerOfTwo) {
                    console.error("Error logging throttled", errorBasics);
                    return;
                }
                const withCountAndRelevace = {
                    ...errorBasics,
                    ...errorExtraInfo,
                    errorCount,
                    isIrrelevant: isIrrelevant || undefined,
                };

                if ((window as any).lastJsCrashForSelenium === errorBasics) {
                    //if to ensure this is still the most recent error
                    (window as any).lastJsCrashForSelenium = withCountAndRelevace;
                }
                fetch('/jserror', {
                    method: "POST",
                    headers: { "Content-Type": "application/json", },
                    body: JSON.stringify(withCountAndRelevace),
                });
            });
    });
}
